import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useProjects } from "@/contexts/ProjectContext";
import { llmApi } from "@/services/rag-service";
import type { IDefaultModel } from "@/types/conversations";
import type { Model } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  ALL_PROJECTS_MODEL_SCOPE,
  PERSONAL_MODEL_SCOPE,
  getModelRequestScope,
  modelQueryKeys,
  modelScopeIdentity,
  type ModelQueryScope,
} from "../model-query-keys";

type LLMConfigsResponse = {
  items: Model[];
  total_count: number;
};

export const useModelQueryScope = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const { currentOrganization, initialized } = useOrganization();
  const { selectedProjects } = useProjects();

  const scope: ModelQueryScope = {
    principalId: currentUser?.id ?? "",
    organizationId: currentOrganization?.id ?? PERSONAL_MODEL_SCOPE,
    projectId:
      selectedProjects.length === 1 && selectedProjects[0] !== "all"
        ? selectedProjects[0]
        : ALL_PROJECTS_MODEL_SCOPE,
  };

  return {
    scope,
    identity: modelScopeIdentity(scope),
    requestScope: getModelRequestScope(scope),
    ready:
      authLoading !== true &&
      initialized === true &&
      Boolean(scope.principalId) &&
      selectedProjects.length > 0,
  };
};

export const useModelsQuery = () => {
  const queryScope = useModelQueryScope();
  const query = useQuery<Model[]>({
    queryKey: modelQueryKeys.list(queryScope.scope),
    queryFn: async () => {
      const response = (await llmApi.getLLMConfigs(
        undefined,
        queryScope.requestScope,
      )) as unknown as LLMConfigsResponse;

      return response.items?.filter((model) => !model.is_embedding_model) ?? [];
    },
    enabled: queryScope.ready,
  });

  return { ...query, ...queryScope };
};

export const useDefaultModelsQuery = () => {
  const queryScope = useModelQueryScope();
  const query = useQuery<{ defaults: IDefaultModel[] }>({
    queryKey: modelQueryKeys.defaults(queryScope.scope),
    queryFn: () => llmApi.getDefaultModels(queryScope.requestScope),
    enabled: queryScope.ready,
  });

  return { ...query, ...queryScope };
};
